// geo.js — MumHQ location & distance layer
// Replaces the old static `distanceMiles` values with real computed distances.
//
// How it works:
//   1. The USER's postcode is geocoded live via postcodes.io (free, no API key).
//   2. Each CAMP's postcode is read out of its `location` string and matched
//      against pre-baked coordinates in campCoords.js (generated once by
//      scripts/geocode-camps.mjs — see that file's header).
//   3. Distance = haversine between the two. No hardcoded numbers anywhere.

import { POSTCODE_COORDS, OUTCODE_COORDS } from "./campCoords";

const API = "https://api.postcodes.io";
const CACHE_KEY = "mumhq_geo_cache_v1";

// ── Postcode parsing ─────────────────────────────────────────────────────────

/** Strip spaces, uppercase. "n16 8ea" -> "N168EA" */
export function normalisePostcode(raw) {
  return String(raw || "").replace(/\s+/g, "").toUpperCase();
}

/** Re-insert the space before the final 3 chars. "N168EA" -> "N16 8EA" */
export function formatPostcode(raw) {
  const c = normalisePostcode(raw);
  if (c.length < 5) return c;
  return c.slice(0, -3) + " " + c.slice(-3);
}

/** True for a complete UK postcode (outward + inward), e.g. "N16 8EA". */
export function isFullPostcode(raw) {
  return /^[A-Z]{1,2}\d{1,2}[A-Z]?\d[A-Z]{2}$/.test(normalisePostcode(raw));
}

/** True for an outward code only, e.g. "N16", "NW3", "SW1A". */
export function isOutcode(raw) {
  return /^[A-Z]{1,2}\d{1,2}[A-Z]?$/.test(normalisePostcode(raw));
}

/** Pull the outward code off any postcode. "N168EA" -> "N16" */
export function outcodeOf(raw) {
  const c = normalisePostcode(raw);
  if (isOutcode(c)) return c;
  return c.slice(0, -3);
}

/**
 * Extract a full postcode from a free-text venue location string.
 * "Canonbury Primary School, N1 2NS" -> "N12NS"
 * Returns null if the string only carries an outward code.
 */
export function extractPostcode(location) {
  if (!location) return null;
  const m = String(location).match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s*(\d[A-Z]{2})\b/i);
  return m ? normalisePostcode(m[1] + m[2]) : null;
}

/** Extract at least an outward code from a location string. */
export function extractOutcode(location) {
  if (!location) return null;
  const full = extractPostcode(location);
  if (full) return outcodeOf(full);
  const m = String(location).match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?)\b/);
  return m ? normalisePostcode(m[1]) : null;
}

// ── Cache (so we don't re-hit the API for a postcode we've already seen) ──────

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); }
  catch { return {}; }
}
function writeCache(obj) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(obj)); } catch { /* quota — ignore */ }
}

// ── User postcode lookup (live, via postcodes.io) ─────────────────────────────

/**
 * Geocode the postcode the user typed into "Your Location".
 * Accepts a FULL postcode (N16 8EA) or an outward code (N16).
 * Resolves to { lat, lon, postcode, district, precise }.
 * Rejects with a readable message on a genuinely invalid postcode —
 * it will NOT silently guess a nearby area (the old bug).
 */
export async function lookupPostcode(input) {
  const clean = normalisePostcode(input);
  if (!clean) throw new Error("Enter a postcode to continue.");

  const cache = readCache();
  if (cache[clean]) return cache[clean];

  let info = null;

  if (isFullPostcode(clean)) {
    const res = await fetch(`${API}/postcodes/${encodeURIComponent(clean)}`);
    if (res.ok) {
      const { result } = await res.json();
      info = {
        lat: result.latitude,
        lon: result.longitude,
        postcode: result.postcode,
        district: result.admin_ward || result.admin_district,
        precise: true,
      };
    }
  }

  // Full postcode not found (or user only gave an outward code) → try the outcode.
  if (!info) {
    const oc = outcodeOf(clean);
    if (isOutcode(oc)) {
      const res = await fetch(`${API}/outcodes/${encodeURIComponent(oc)}`);
      if (res.ok) {
        const { result } = await res.json();
        info = {
          lat: result.latitude,
          lon: result.longitude,
          postcode: oc,
          district: (result.admin_ward && result.admin_ward[0]) || (result.admin_district && result.admin_district[0]) || oc,
          precise: false,
        };
      }
    }
  }

  // Offline / API down → fall back to our own baked outcode table, but only on
  // an EXACT outcode match. Never a fuzzy prefix guess.
  if (!info) {
    const oc = outcodeOf(clean);
    const local = OUTCODE_COORDS[oc];
    if (local) {
      info = { lat: local.lat, lon: local.lon, postcode: oc, district: local.district || oc, precise: false };
    }
  }

  if (!info) {
    throw new Error(`"${formatPostcode(clean)}" isn't a postcode we recognise. Check it and try again.`);
  }

  cache[clean] = info;
  writeCache(cache);
  return info;
}

// ── Camp coordinates ─────────────────────────────────────────────────────────

/**
 * Resolve coordinates for an activity/camp.
 * Prefers its exact postcode; falls back to the centre of its outward code.
 * Returns { lat, lon, precise } or null if we have nothing to go on.
 */
export function coordsForActivity(act) {
  if (!act) return null;

  // Some entries may already carry explicit coords.
  if (typeof act.lat === "number" && typeof act.lon === "number") {
    return { lat: act.lat, lon: act.lon, precise: true };
  }

  const full = extractPostcode(act.location);
  if (full && POSTCODE_COORDS[full]) {
    const c = POSTCODE_COORDS[full];
    return { lat: c[0], lon: c[1], precise: true };
  }

  const oc = extractOutcode(act.location) || normalisePostcode(act.postcodeSector);
  if (oc && OUTCODE_COORDS[oc]) {
    const c = OUTCODE_COORDS[oc];
    return { lat: c.lat, lon: c.lon, precise: false };
  }

  return null;
}

// ── Distance ─────────────────────────────────────────────────────────────────

/** Great-circle distance in miles. */
export function haversine(lat1, lon1, lat2, lon2) {
  const R = 3958.8, toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Distance from the user to an activity.
 * Returns { miles, precise } or null when either end has no coordinates.
 * `precise` is false when we had to fall back to an outward-code centre —
 * the UI shows a "~" so we never overstate what we actually know.
 */
export function distanceTo(userInfo, act) {
  if (!userInfo) return null;
  const c = coordsForActivity(act);
  if (!c) return null;
  const miles = haversine(userInfo.lat, userInfo.lon, c.lat, c.lon);
  return { miles, precise: c.precise && userInfo.precise !== false };
}

/**
 * Human label for a distance.
 * Deliberately does NOT claim walk or drive times: a straight line is not a
 * route, and in London a 0.4 mi line can be a 20 min trip round a rail cutting
 * or across the river. We state the distance we can actually verify.
 */
export function distanceLabel(d) {
  if (!d) return null;
  const approx = d.precise ? "" : "~";
  const m = d.miles;
  if (m < 0.1) return `${approx}on your doorstep`;
  if (m < 10) return `${approx}${m.toFixed(1)} mi`;
  return `${approx}${Math.round(m)} mi`;
}

/** Badge colouring — green when close, amber when far. */
export function distanceBadgeStyle(miles, COLORS) {
  if (miles <= 1) return { bg: COLORS.greenLight, color: "#2E6B4F", border: COLORS.green };
  if (miles <= 3) return { bg: COLORS.blueGhost, color: COLORS.blue, border: COLORS.blueLight };
  return { bg: COLORS.orangeLight, color: "#8B5000", border: COLORS.orange };
}
